import {useCallback, useEffect, useRef, useState} from 'react';

export const useVideoPlayer = (autoPlay = false) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  const [isPlaying, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [buffered, setBuffered] = useState(0);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;

    if (!video) return;

    if (video.paused) {
      void video.play();
      setPlaying(true);
    } else {
      video.pause();
      setPlaying(false);
    }
  }, []);

  const seek = useCallback((time: number) => {
    const video = videoRef.current;

    if (!video) return;

    video.currentTime = time;
    setCurrentTime(time);
  }, []);

  useEffect(() => {
    const video = videoRef.current;

    if (!video) return;

    const onLoaded = () => setDuration(video.duration);

    const onTime = () => setCurrentTime(video.currentTime);

    const onProgress = () => {
      if (!video.buffered.length) return;

      setBuffered(video.buffered.end(video.buffered.length - 1));
    };

    const onPlay = () => setPlaying(true);

    const onPause = () => setPlaying(false);

    const onEnded = () => setPlaying(false);

    video.addEventListener('loadedmetadata', onLoaded);
    video.addEventListener('timeupdate', onTime);
    video.addEventListener('progress', onProgress);
    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('ended', onEnded);

    if (autoPlay) {
      video.play();
    }

    return () => {
      video.removeEventListener('loadedmetadata', onLoaded);
      video.removeEventListener('timeupdate', onTime);
      video.removeEventListener('progress', onProgress);
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('ended', onEnded);
    };
  }, [autoPlay]);

  return {
    videoRef,
    isPlaying,
    duration,
    currentTime,
    buffered,
    togglePlay,
    seek,
  };
};
